import { SYSTEM_HOME_PAGE_URL } from '@/app/consts/systemHomePage';
import { Navigate, Route, Routes, useLocation } from 'react-router-dom';
import { MasterLayout } from '../../_metronic/layout/MasterLayout';
import ReportPageSkeleton from '../pages/reports/components/ReportPageSkeleton';
import SuspensedView from '../components/layout/SuspensedView';
import { lazyWithRetry } from '../helpers/lazyWithRetry';
import { RESOURCES } from '../casl/resources';
import { AbilityProtectedRoute } from './AbilityProtectedRoute';

const CatalogWrapper = lazyWithRetry('CatalogWrapper', () => import('../pages/catalog/CatalogWrapper'));
const FileManagerListPage = lazyWithRetry(
  'FileManagerListPage',
  () => import('../pages/file-manager/FileManagerListPage'),
);
const FinanceWrapper = lazyWithRetry('FinanceWrapper', () => import('../pages/finance/FinanceWrapper'));
const HomeWrapper = lazyWithRetry('HomeWrapper', () => import('../pages/home/HomeWrapper'));
const NotificationsWrapper = lazyWithRetry(
  'NotificationsWrapper',
  () => import('../pages/notifications/NotificationsWrapper'),
);
const PreferencesWrapper = lazyWithRetry('PreferencesWrapper', () => import('../pages/preferences/PreferencesWrapper'));
const RegisterWrapper = lazyWithRetry('RegisterWrapper', () => import('../pages/registrations/RegisterWrapper'));
const CrmWrapper = lazyWithRetry('CrmWrapper', () => import('../pages/crm/CrmWrapper'));
const SalesWrapper = lazyWithRetry('SalesWrapper', () => import('../pages/sales/SalesWrapper'));
const SupplyWrapper = lazyWithRetry('SupplyWrapper', () => import('../pages/supply/SupplyWrapper'));
const AccountantSpaceWrapper = lazyWithRetry(
  'AccountantSpaceWrapper',
  () => import('../pages/accountant-space/AccountantSpaceWrapper'),
);
const ManagerSpaceWrapper = lazyWithRetry(
  'ManagerSpaceWrapper',
  () => import('../pages/manager-space/ManagerSpaceWrapper'),
);
const ReportsWrapper = lazyWithRetry('ReportsWrapper', () => import('../pages/reports/ReportsWrapper'));
const TicketsWrapper = lazyWithRetry('TicketsWrapper', () => import('../pages/tickets/TicketsWrapper'));
const TrainingsWrapper = lazyWithRetry('TrainingsWrapper', () => import('../pages/trainings/TrainingsWrapper'));
const ChecklistsWrapper = lazyWithRetry('ChecklistsWrapper', () => import('../pages/checklists/ChecklistsWrapper'));
const SupportPage = lazyWithRetry('SupportPage', () => import('../pages/support/SupportPage'));

const SystemUserRoutes = () => {
  const { pathname } = useLocation();
  const homeViewKey = pathname.startsWith('/inicio/dashboard/') ? '/inicio/dashboard' : pathname;

  return (
    <Routes>
      <Route element={<MasterLayout />}>
        {/* Bare "/" → home. Post-login redirect is centralized in AppRoutes (auth/*). */}
        <Route index element={<Navigate replace to={SYSTEM_HOME_PAGE_URL} />} />
        {/* Home - Always accessible */}
        <Route
          element={
            <SuspensedView resetKey={homeViewKey}>
              <HomeWrapper />
            </SuspensedView>
          }
          path="/inicio/*"
        />

        {/* Catalog */}
        <Route
          element={
            <SuspensedView>
              <CatalogWrapper />
            </SuspensedView>
          }
          path="/cadastros/catalogo/*"
        />

        {/* Sales */}
        <Route
          element={
            <SuspensedView>
              <SalesWrapper />
            </SuspensedView>
          }
          path="/vendas/*"
        />

        {/* CRM */}
        <Route
          element={
            <SuspensedView>
              <CrmWrapper />
            </SuspensedView>
          }
          path="/crm/*"
        />

        {/* Supply */}
        <Route
          element={
            <SuspensedView>
              <SupplyWrapper />
            </SuspensedView>
          }
          path="/suprimentos/*"
        />

        {/* Accountant Space */}
        <Route
          element={
            <SuspensedView>
              <AccountantSpaceWrapper />
            </SuspensedView>
          }
          path="/espaco-contador/*"
        />

        {/* Manager Space */}
        <Route
          element={
            <SuspensedView>
              <ManagerSpaceWrapper />
            </SuspensedView>
          }
          path="/espaco-gestor/*"
        />

        {/* Trainings */}
        <Route
          element={
            <SuspensedView>
              <TrainingsWrapper />
            </SuspensedView>
          }
          path="/treinamentos/*"
        />

        {/* Preferences */}
        <Route
          element={
            <SuspensedView>
              <PreferencesWrapper />
            </SuspensedView>
          }
          path="/preferencias/*"
        />

        {/* Registrations */}
        <Route
          element={
            <SuspensedView>
              <RegisterWrapper />
            </SuspensedView>
          }
          path="/cadastros/*"
        />

        {/* Finance */}
        <Route
          element={
            <SuspensedView>
              <FinanceWrapper />
            </SuspensedView>
          }
          path="/financas/*"
        />

        {/* Reports */}
        <Route
          element={
            <SuspensedView fallback={<ReportPageSkeleton />}>
              <ReportsWrapper />
            </SuspensedView>
          }
          path="/relatorios/*"
        />

        {/* Notifications - Always accessible */}
        <Route
          element={
            <SuspensedView>
              <NotificationsWrapper />
            </SuspensedView>
          }
          path="/notificacao/*"
        />

        {/* File Manager */}
        <Route
          element={
            <AbilityProtectedRoute permission={RESOURCES.GERENCIAR_ARQUIVOS}>
              <SuspensedView>
                <FileManagerListPage />
              </SuspensedView>
            </AbilityProtectedRoute>
          }
          path="/arquivos"
        />

        <Route
          element={
            <SuspensedView>
              <ChecklistsWrapper />
            </SuspensedView>
          }
          path="/suprimentos/checklists/*"
        />

        {/* Tickets mock */}
        <Route
          element={
            <SuspensedView>
              <TicketsWrapper />
            </SuspensedView>
          }
          path="/chamados/*"
        />

        {/* Support - Always accessible */}
        <Route
          element={
            <SuspensedView>
              <SupportPage />
            </SuspensedView>
          }
          path="/suporte"
        />
        <Route element={<Navigate to="/error/404" />} path="*" />
      </Route>
    </Routes>
  );
};

export { SystemUserRoutes };
